// Avatar initials + display name derived from the best info we have about a
// user.
//
// Sign in with Apple only returns the user's name on the FIRST-EVER
// authorization of the app for that Apple ID — never on re-auth (even after
// the MixGoat account was deleted and recreated). When the name is absent the
// auth service stores the placeholder 'User', which used to render as a lone
// "U" avatar. Apple "Hide My Email" users can also arrive with no name and a
// synthesized `<sub>@apple.private` email.
//
// So: prefer a real name, else fall back to the email's first character /
// local part, and treat the 'User' placeholder + synthesized Apple-private
// emails as "no info".

const PLACEHOLDER_FIRST_NAMES = new Set(['user', '']);

function realName(user) {
    const fn = (user?.first_name || '').trim();
    const ln = (user?.last_name || '').trim();
    if (!fn || PLACEHOLDER_FIRST_NAMES.has(fn.toLowerCase())) return null;
    return { fn, ln };
}

function usableEmail(user) {
    const email = (user?.email || '').trim();
    if (!email) return null;
    // Our own synthesized placeholder for Apple "Hide My Email" with no name —
    // nothing human-readable to show.
    if (/@apple\.private$/i.test(email)) return null;
    return email;
}

export function userInitials(user) {
    if (!user) return '?';
    const name = realName(user);
    if (name) {
        const init = `${name.fn[0] || ''}${name.ln[0] || ''}`.toUpperCase();
        if (init) return init;
    }
    const email = usableEmail(user);
    if (email) {
        const ch = email.match(/[a-z0-9]/i);
        if (ch) return ch[0].toUpperCase();
    }
    return '?';
}

export function userDisplayName(user) {
    if (!user) return '';
    const name = realName(user);
    if (name) return `${name.fn} ${name.ln}`.trim();
    const email = usableEmail(user);
    if (email) return email.split('@')[0];
    return 'You';
}

// Real first/last name only — '' when the account has no usable name (email
// prefix / "You" fallbacks are fine for a menu, wrong printed on a share card).
export function userRealName(user) {
    const name = realName(user);
    return name ? `${name.fn} ${name.ln}`.trim() : '';
}
